import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { adminAxios, formatApiError } from "../../lib/adminApi";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";

export default function AdminContent() {
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminAxios.get("/content");
        setC(data || {});
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminAxios.put("/admin/content", c);
      toast.success("Site content updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !c) {
    return <div className="py-20 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#1C6B8A]" /></div>;
  }

  const f = (key, label, Component = Input, extraProps = {}) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Component
        value={c[key] || ""}
        onChange={(e) => setC({ ...c, [key]: e.target.value })}
        data-testid={`content-${key}`}
        {...extraProps}
      />
    </div>
  );

  return (
    <div data-testid="admin-content-page" className="space-y-6">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Site content</h1>
        <p className="text-slate-500 text-sm mt-1">Update address, contact, social links and about text.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 card-soft space-y-4 max-w-3xl">
        {f("tagline", "Tagline")}
        {f("address", "Business address", Textarea, { rows: 2 })}

        <div className="grid md:grid-cols-2 gap-4">
          {f("sales_phone", "Sales phone (10-digit)")}
          {f("service_phone", "Service phone (10-digit)")}
        </div>
        {f("email", "Contact email")}

        <div className="grid md:grid-cols-3 gap-4">
          {f("facebook", "Facebook URL")}
          {f("instagram", "Instagram URL")}
          {f("google_business", "Google Business URL")}
        </div>

        {f("about_short", "About — short", Textarea, { rows: 2 })}
        {f("about_long", "About — long", Textarea, { rows: 4 })}

        <button
          onClick={save}
          disabled={saving}
          data-testid="content-save-btn"
          className="h-11 px-6 rounded-full brand-btn text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save changes
        </button>
      </div>
    </div>
  );
}
